import { useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useSyncTeams, type SyncTeamsResult } from '../queries'
import styles from './SeedRostersForm.module.css'

type SyncTeamsFormProps = {
  competitionId: string
}

export function SyncTeamsForm({ competitionId }: SyncTeamsFormProps) {
  const [season,     setSeason]     = useState(new Date().getFullYear())
  const [leagueId,   setLeagueId]   = useState('1')
  const [lastResult, setLastResult] = useState<SyncTeamsResult | null>(null)

  const sync = useSyncTeams()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!leagueId.trim()) return
    sync.mutate(
      { competitionId, season, leagueId: leagueId.trim() },
      { onSuccess: (result) => setLastResult(result) },
    )
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>1 · Importar Times via API-Football</h3>
      <p className={styles.hint}>
        Cria os times desta competição a partir da API-Football e atribui o{' '}
        <code>grupo</code> de cada um pela tabela de classificação. Rode antes do calendário.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Temporada</span>
            <input
              type="number"
              className={styles.input}
              value={season}
              min={2020}
              max={2030}
              onChange={(e) => setSeason(Number(e.target.value))}
              disabled={sync.isPending}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>League ID (API-Football)</span>
            <input
              type="text"
              className={styles.input}
              value={leagueId}
              placeholder="ex: 1"
              onChange={(e) => setLeagueId(e.target.value)}
              disabled={sync.isPending}
            />
          </label>
        </div>

        <button type="submit" className={styles.btn} disabled={sync.isPending}>
          {sync.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <RefreshCw size={14} />}
          {sync.isPending ? 'Importando…' : 'Importar Times'}
        </button>
      </form>

      {lastResult && <SyncTeamsResultView result={lastResult} />}
    </div>
  )
}

function SyncTeamsResultView({ result }: { result: SyncTeamsResult }) {
  return (
    <div className={styles.result}>
      <div className={styles.resultTitle}>Último resultado</div>
      <div className={styles.stats}>
        <Stat label="Times na API"   value={result.teamsFromApi} muted />
        <Stat label="Times criados"  value={result.teamsCreated} accent />
        <Stat label="Atualizados"    value={result.teamsUpdated} />
        <Stat label="Grupos"         value={result.groupsAssigned} accent />
      </div>

      {result.warnings.length > 0 && (
        <div className={styles.warnings}>
          <div className={styles.warningsHeader}>
            <AlertTriangle size={12} />
            {result.warnings.length} aviso{result.warnings.length === 1 ? '' : 's'}
          </div>
          <ul className={styles.warningsList}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat({
  label, value, accent, muted,
}: {
  label:   string
  value:   number
  accent?: boolean
  muted?:  boolean
}) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={
        accent ? styles.statValueAccent :
        muted  ? styles.statValueMuted  :
        styles.statValue
      }>
        {value}
      </span>
    </div>
  )
}
